// The detail table below the chart. A Gantt is good at showing shape and bad
// at showing a precise value; this is where the precise value lives.
// Sortable by clicking a header. Every date and number column carries
// tabular-nums so the digits line up down the column.
import { ArrowDown, ArrowUp } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../lib/shadcn/table'
import { Badge } from '../../lib/shadcn/badge'
import { Button } from '../../lib/shadcn/button'
import {
  formatDate,
  formatOwner,
  formatVariance,
  sortDevPlanItems,
  statusLabel,
  STATUS_BADGE_VARIANT,
  type GanttItem,
  type SortColumn,
} from '../../utils/devPlan'

const COLUMNS: { key: SortColumn; label: string }[] = [
  { key: 'ref', label: 'Ref' },
  { key: 'phase_label', label: 'Phase' },
  { key: 'title', label: 'Title' },
  { key: 'status', label: 'Status' },
  { key: 'planned_end', label: 'Planned end' },
  { key: 'variance_days', label: 'Variance' },
  { key: 'owner_name', label: 'Owner' },
]

export function DevPlanTable({
  items,
  sortColumn,
  sortDirection,
  onSort,
  onSelect,
  selectedRef,
}: {
  items: GanttItem[]
  sortColumn: SortColumn
  sortDirection: 'asc' | 'desc'
  onSort: (column: SortColumn) => void
  onSelect: (ref: string) => void
  selectedRef: string | null
}) {
  const sorted = sortDevPlanItems(items, sortColumn, sortDirection)

  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            {COLUMNS.map((c) => (
              <TableHead key={c.key}>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-auto gap-1 px-0 font-medium"
                  onClick={() => onSort(c.key)}
                >
                  {c.label}
                  {sortColumn === c.key &&
                    (sortDirection === 'asc' ? (
                      <ArrowUp className="h-3 w-3" />
                    ) : (
                      <ArrowDown className="h-3 w-3" />
                    ))}
                </Button>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.length === 0 && (
            <TableRow>
              <TableCell colSpan={COLUMNS.length} className="text-center text-sm text-muted-foreground">
                Nothing matches the current filters.
              </TableCell>
            </TableRow>
          )}
          {sorted.map((i) => (
            <TableRow
              key={i.item_id}
              onClick={() => onSelect(i.ref)}
              className={i.ref === selectedRef ? 'bg-muted/50' : undefined}
            >
              <TableCell className="tabular-nums">{i.ref}</TableCell>
              <TableCell>{i.phase_label ?? i.phase_key}</TableCell>
              <TableCell className="max-w-xs truncate">{i.title}</TableCell>
              <TableCell>
                <Badge variant={STATUS_BADGE_VARIANT[i.status] ?? 'outline'}>
                  {statusLabel(i.status)}
                </Badge>
                {i.is_overdue && (
                  <Badge variant="destructive" className="ml-1">
                    overdue
                  </Badge>
                )}
              </TableCell>
              <TableCell className="tabular-nums">{formatDate(i.planned_end)}</TableCell>
              <TableCell className="tabular-nums">{formatVariance(i.variance_days)}</TableCell>
              <TableCell>{formatOwner(i.owner_name)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
